"use client";

import React from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import {
  format,
  eachDayOfInterval,
  startOfMonth,
  startOfWeek,
  endOfMonth,
  isBefore,
  isSameDay,
  addDays,
  addMonths,
  endOfWeek,
  isSameMonth,
  startOfDay,
  isAfter,
} from "date-fns";
import Image from "next/image";
import { FaInfoCircle } from "react-icons/fa";
import { isDaytime, getHoursUntilSlot, isSlotInPast, isSlotRefundEligible } from "@/utils/bookingUtils";

interface BookingComponentProps {
  currentMonth: Date;
  currentStartDate: Date;
  setCurrentMonth: (month: Date) => void;
  handlePreviousWeek: () => void;
  handleNextWeek: () => void;
  handleNextDay: () => void;
  handlePreviousDay: () => void;
  selectedDay: Date | null;
  selectedTimeSlot: string | null;
  filteredAvailability: {
    id: string;
    startTime: string;
    endTime: string;
    isDisabled?: boolean;
    disabledReason?: string;
  }[];
  availabilityLoading: boolean;
  handlePreviousMonth: () => void;
  handleNextMonth: () => void;
  setSelectedDay: (day: Date) => void;
  setSelectedTimeSlot: (slotId: string) => void;
  handleConfirmBooking: () => void;
  formatTime: (time: string) => string;
  setCurrentStartDate: (date: Date) => void;
  onFindNextAvailable?: () => void;
}

const BookingComponent: React.FC<BookingComponentProps> = ({
  currentMonth,
  setCurrentMonth,
  currentStartDate,
  handlePreviousWeek,
  handleNextWeek,
  selectedDay,
  selectedTimeSlot,
  filteredAvailability,
  availabilityLoading,
  handlePreviousDay,
  handleNextDay,
  handlePreviousMonth,
  handleNextMonth,
  setSelectedDay,
  setSelectedTimeSlot,
  handleConfirmBooking,
  formatTime,
  setCurrentStartDate,
  onFindNextAvailable,
}) => {
  const today = new Date();

  const goToToday = () => {
    const today = new Date();
    setCurrentStartDate(startOfWeek(today, { weekStartsOn: 0 }));
    setCurrentMonth(startOfMonth(today));
    setSelectedDay(today);
  };

  const getHeaderMonthDisplay = () => {
    // Get the first and last days of the current week
    const weekStart = startOfWeek(currentStartDate, { weekStartsOn: 0 });
    const weekEnd = endOfWeek(currentStartDate, { weekStartsOn: 0 });

    // Check if all days belong to the same month
    let spansTwoMonths = false;
    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i);
      if (!isSameMonth(weekStart, day)) {
        spansTwoMonths = true;
        break;
      }
    }

    // Display one or two months based on the result
    if (spansTwoMonths) {
      return `${format(weekStart, "MMM")} / ${format(weekEnd, "MMM yyyy")}`;
    } else {
      return format(weekStart, "MMMM yyyy");
    }
  };


  return (
    <>
      {/* 1st Section - Calendar & Booking */}
      <div className="mt-10 flex flex-col items-center text-[#27335D]">
        {/* Month Navigation */}
        <div className="flex w-full flex-row items-center justify-center">
          <div className="flex flex-row items-center gap-3.5 rounded-t-5xl border-2 border-b-0 border-[#27335D] bg-[#75C9EB] px-3 py-1 text-base font-semibold md:text-lg lg:text-xl">
            <button
              data-testid="bookingPreviousMonthButton"
              onClick={handlePreviousMonth}
              disabled={isSameMonth(currentMonth, new Date())}
              className={`flex items-center justify-center ${
                isSameMonth(currentMonth, new Date())
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              }`}
            >
              <IoIosArrowBack className="text-sm md:text-lg lg:text-xl" />
            </button>

            <div className="text-sm md:text-base lg:text-lg">
              {getHeaderMonthDisplay()}
            </div>

            <button
              data-testid="bookingNextMonthButton"
              onClick={handleNextMonth}
              className="flex items-center justify-center"
            >
              <IoIosArrowForward className="text-sm md:text-lg lg:text-xl" />
            </button>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="container mx-auto w-full max-w-5xl">
          {/* Week Navigation Buttons */}
          <div className="rounded-t-5xl bg-[#27335d] px-6 py-3 font-bold text-[#F6C65A] md:px-10 md:py-5">
            {/* Navigation for sm & xsm → Show only 1 day at a time */}
            <div className="flex items-center justify-between md:hidden">
              <button
                data-testid="bookingPreviousDayButton"
                onClick={handlePreviousDay}
                disabled={isBefore(
                  selectedDay || new Date(),
                  addDays(new Date(), 0),
                )}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold text-white transition-all ${
                  isBefore(selectedDay || new Date(), addDays(new Date(), 0))
                    ? "cursor-not-allowed text-gray-500 opacity-50"
                    : "cursor-pointer hover:text-[#E9E9EA]"
                }`}
              >
                <IoIosArrowBack />
              </button>

              {/* Display Single Day */}
              <div className="flex flex-col items-center">
                <div className="text-lg font-bold text-[#75C9EB]">
                  {format(selectedDay || new Date(), "EEE")}
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#75C9EB] font-bold text-[#27335d]">
                  {format(selectedDay || new Date(), "d")}
                </div>
              </div>

              <button
                data-testid="bookingNextDayButton"
                onClick={handleNextDay}
                className="flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold text-white transition-all hover:text-[#E9E9EA]"
              >
                <IoIosArrowForward />
              </button>
            </div>

            {/* Navigation for md, lg, xl → Show full week */}
            <div className="hidden items-center justify-between md:flex">
              <button
                data-testid="bookingPreviousWeekButton"
                onClick={handlePreviousWeek}
                disabled={isBefore(
                  currentStartDate,
                  startOfWeek(new Date(), { weekStartsOn: 1 }),
                )}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold transition-all md:h-10 md:w-10 md:text-2xl lg:h-12 lg:w-12 lg:text-3xl ${
                  isBefore(
                    currentStartDate,
                    startOfWeek(new Date(), { weekStartsOn: 1 }),
                  )
                    ? "cursor-not-allowed text-gray-500 opacity-50"
                    : "cursor-pointer text-white hover:text-[#E9E9EA]"
                }`}
              >
                <IoIosArrowBack />
              </button>

              {/* Days of the Week */}
              <div className="flex flex-row items-center justify-center gap-4 md:gap-6 lg:gap-8">
                {Array.from({ length: 7 }).map((_, index) => {
                  const day = addDays(currentStartDate, index);
                  const isDisabled =
                    isBefore(day, today) && !isSameDay(day, today);

                  return (
                    <button
                      key={day.toString()}
                      data-testid={`bookingWeekDay${format(day, "EEEE")}`}
                      onClick={() => !isDisabled && setSelectedDay(day)}
                      disabled={isDisabled}
                      className={`flex flex-col items-center space-y-2 ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                    >
                      <div
                        className={`text-sm font-bold md:text-lg lg:text-xl ${
                          selectedDay && isSameDay(day, selectedDay)
                            ? "text-[#52BBE6]"
                            : isDisabled
                              ? "text-gray-400"
                              : "text-white transition-all hover:text-[#E9E9EA]"
                        }`}
                      >
                        {format(day, "EEE")}
                      </div>

                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full font-bold text-[#27335d] md:h-10 md:w-10 lg:h-12 lg:w-12 ${
                          selectedDay && isSameDay(day, selectedDay)
                            ? "bg-[#52BBE6]"
                            : isDisabled
                              ? "bg-gray-200 text-gray-400"
                              : "bg-white transition-all hover:bg-[#E9E9EA]"
                        }`}
                      >
                        {format(day, "d")}
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                data-testid="bookingNextWeekButton"
                onClick={handleNextWeek}
                className="flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold text-white transition-all hover:text-[#E9E9EA] md:h-10 md:w-10 md:text-2xl lg:h-12 lg:w-12 lg:text-3xl"
              >
                <IoIosArrowForward />
              </button>
            </div>
          </div>

          {/* Time Selection & Booking Section */}
          <div className="flex min-h-80 flex-col justify-between rounded-b-5xl border-2 border-[#27335D] bg-white p-4 md:min-h-96 md:p-6">
            <div className="flex flex-col justify-between text-start text-[#F6C65A]">
              {/* Time Slots */}
              <div>
                {selectedDay ? (
                  <p className="mb-4 text-sm font-bold text-[#27335d] xsm:text-sm sm:text-base md:text-lg lg:text-xl">
                    Select available time on{" "}
                    <span className="text-[#4887F5]">
                      {format(selectedDay, "EEE, MMM d")}
                    </span>
                  </p>
                ) : (
                  <p className="text-base font-bold text-[#27335d] md:text-lg lg:text-xl">
                    Select a date to view available times
                  </p>
                )}

                <div
                  data-testid="bookingAvailabilityContainer"
                  data-loading={availabilityLoading ? "true" : "false"}
                >
                  {availabilityLoading ? (
                    <div className="mt-4 text-sm text-[#F6C65A] md:text-base">
                      Loading availability...
                    </div>
                  ) : (
                    <div className="mt-3 space-y-4">
                      {filteredAvailability.length > 0 ? (
                        <div className="flex flex-wrap justify-center gap-2 md:justify-start">
                          {filteredAvailability.map((slot) => {
                            const slotDay = selectedDay || new Date();
                            const currentTime = format(new Date(), "HH:mm");
                            const isPast = isSlotInPast(slot.startTime, slotDay, currentTime);
                            const isDisabled = isPast || Boolean(slot.isDisabled);
                            const hoursUntil = getHoursUntilSlot(slot.startTime, slotDay);

                            return (
                              <button
                                key={slot.id}
                                data-testid="bookingTimeSlotButton"
                                data-hours-until={hoursUntil}
                                data-refund-eligible={isSlotRefundEligible(slot.startTime, slotDay) ? "true" : "false"}
                                onClick={() =>
                                  !isDisabled && setSelectedTimeSlot(slot.id)
                                }
                                disabled={isDisabled}
                                title={slot.disabledReason}
                                className={`rounded-3xl border-2 px-3 py-2 text-xs font-medium transition-colors md:text-sm lg:text-base ${
                                  isDisabled
                                    ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                    : selectedTimeSlot === slot.id
                                      ? "border-[#27335d] bg-[#8C8D8F] text-[#27335d]"
                                      : "border-[#27335d] bg-white text-[#27335d] transition-all hover:bg-[#E9E9EA]"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <Image
                                    src={`/images/svg/${isDaytime(slot.startTime) ? "sun" : "moon"}.svg`}
                                    alt="Time Icon"
                                    width={10}
                                    height={10}
                                    className="h-4 w-4 md:h-5 md:w-5"
                                  />
                                  {formatTime(slot.startTime)} -{" "}
                                  <Image
                                    src={`/images/svg/${isDaytime(slot.endTime) ? "sun" : "moon"}.svg`}
                                    alt="Time Icon"
                                    width={10}
                                    height={10}
                                    className="h-4 w-4 md:h-5 md:w-5"
                                  />
                                  {formatTime(slot.endTime)}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p data-testid="bookingNoSlotsMessage" className="mt-2 text-sm font-semibold text-[#EF7E74] md:text-base">
                          No available slots for this day.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 55-minute lesson note */}
            <p
              data-testid="slotPickerLessonDurationNote"
              className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400"
            >
              <FaInfoCircle className="h-3 w-3 shrink-0" />
              Each hour slot is a 55-minute lesson.
            </p>

            {/* Confirm Button (Now Centered at Bottom) */}
            <div className="mt-auto flex justify-center pt-6">
              <button
                data-testid="bookingConfirmButton"
                onClick={handleConfirmBooking}
                disabled={!selectedTimeSlot}
                className={`rounded-full px-5 py-2 font-bold transition-colors md:px-6 md:py-3 lg:px-7 lg:py-4 ${
                  selectedTimeSlot
                    ? "cursor-pointer border-4 border-[#F6C65A] bg-[#27335d] text-[#F6C65A] hover:bg-[#F6C65A] hover:text-[#27335d]"
                    : "cursor-not-allowed border-4 border-gray-500 bg-gray-500 text-gray-300"
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
        {/* Go to Today / Find Next Available Buttons */}
        <div className="mt-4 flex justify-center gap-3">
          <button
            onClick={goToToday}
            className="rounded-full border-2 border-[#27335d] bg-white px-4 py-2 font-bold text-[#27335d] transition hover:bg-[#E9E9EA] active:scale-95"
          >
            Today
          </button>
          {onFindNextAvailable && (
            <button
              data-testid="bookingFindNextAvailableButton"
              onClick={onFindNextAvailable}
              className="rounded-full border-2 border-[#27335d] bg-[#27335d] px-4 py-2 font-bold text-white transition hover:bg-[#3a4a7a] active:scale-95"
            >
              Find Next Available
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default BookingComponent;
